import { Component, EventEmitter, OnDestroy, OnInit, Output, Input } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { Benachrichtigung } from '../classes/benachrichtigung';
import { BenachrichtigungenService } from '../benachrichtigungen.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { BenachrichtigungTyp } from '../classes/benachrichtigung-typ';
import { KieneUtils } from '../../utils/KieneUtils';
import { Subscription } from 'rxjs';
import { MessageService } from '../../services/message-service/message.service';
import { BenachrichtigungClickEvent } from '../classes/benachrichtigung-click-event';
import { MatDialog } from '@angular/material/dialog';
import { BenachrichtigungDialogComponent } from './../benachrichtigung-dialog/benachrichtigung-dialog.component';
import { CreateTicketDialogComponent } from '../../components/create-ticket-dialog/create-ticket-dialog.component';

@Component({
    selector: 'kiene-benachrichtigung-uebersicht',
    templateUrl: './benachrichtigung-uebersicht.component.html',
    styleUrls: ['./benachrichtigung-uebersicht.component.scss'],
})
export class BenachrichtigungUebersichtComponent implements OnInit, OnDestroy {
    benachrichtigungen: Benachrichtigung[] = [];
    isLoading = true;
    isSaving = false;

    onlyUnreadBenachrichtigungen = true;
    searchCtrl: UntypedFormControl = new UntypedFormControl();
    @Output('benachrichtigungRead')
    benachrichtigungRead: EventEmitter<Benachrichtigung> = new EventEmitter<Benachrichtigung>();
    benachrichtigungsTyp = BenachrichtigungTyp;
    scrollDisabled = false;
    @Input() showSupportButton = true;
    getBenachrichtigungenSub: Subscription;
    reloadSub: Subscription;
    interval: any;
    private offset = 0;
    private limit = 10;

    constructor(
        private readonly benachrichtigungenService: BenachrichtigungenService,
        private readonly messageService: MessageService,
        private readonly dialog: MatDialog,
    ) {}

    ngOnDestroy(): void {
        clearInterval(this.interval);
        this.reloadSub?.unsubscribe();
    }

    ngOnInit(): void {
        this.leseBenachrichtigungen();

        this.interval = setInterval(() => {
            this.leseBenachrichtigungen();
        }, 1000 * 60);

        this.searchCtrl.valueChanges.pipe(debounceTime(500), distinctUntilChanged()).subscribe(() => {
            this.offset = 0;
            this.benachrichtigungen = [];
            this.search();
        });

        this.reloadSub = this.benachrichtigungenService.watchReload().subscribe({
            next: (reload: boolean) => {
                if (reload) {
                    this.leseBenachrichtigungen();
                }
            },
        });
    }

    leseBenachrichtigungen() {
        if (this.onlyUnreadBenachrichtigungen) {
            this.filterUnread(true);
        } else {
            this.loadAllNotifications();
        }
    }

    loadAllNotifications() {
        this.isLoading = true;
        const filter = { offset: 0, limit: this.limit };
        if (this.searchCtrl.value) {
            filter['search'] = this.searchCtrl.value;
        }
        this.getBenachrichtigungenSub?.unsubscribe();
        this.getBenachrichtigungenSub = this.benachrichtigungenService.getBenachrichtigungen(filter).subscribe({
            next: notifications => {
                this.benachrichtigungenService.triggerReload();
                this.isLoading = false;
                // add or replace
                if (this.benachrichtigungen.length === 0) {
                    this.benachrichtigungen = <Benachrichtigung[]>notifications.records;
                } else {
                    notifications.records.forEach((notification: Benachrichtigung) => {
                        const i = this.benachrichtigungen.findIndex(b => b.bb_id === notification.bb_id);
                        if (i >= 0) {
                            this.benachrichtigungen[i] = notification;
                        } else {
                            this.benachrichtigungen.push(notification);
                        }
                    });
                    this.benachrichtigungen.sort(
                        (a, b) => new Date(b.erstellt).getTime() - new Date(a.erstellt).getTime(),
                    );
                }
            },
        });
    }

    markAsRead(benachrichtigung: Benachrichtigung) {
        if (benachrichtigung.gelesen == 0) {
            this.benachrichtigungenService.markBenachrichtigungAsRead(benachrichtigung).subscribe({
                next: () => {
                    this.benachrichtigungenService.triggerReload();
                },
                error: err => {
                    this.messageService.errorMessage('Fehler!', err);
                },
            });
        }
        this.benachrichtigungenService.fireBenachrichtiungClickEvent(new BenachrichtigungClickEvent(benachrichtigung));
    }

    formatDate(createdAt: Date) {
        return KieneUtils.beautifyDate(createdAt);
    }

    filterUnread(checked: boolean) {
        this.offset = 0;
        this.isLoading = true;
        this.onlyUnreadBenachrichtigungen = checked;
        this.getBenachrichtigungenSub?.unsubscribe();
        if (checked) {
            this.getBenachrichtigungenSub = this.benachrichtigungenService
                .getBenachrichtigungen({ gelesen: 0, offset: this.offset, limit: this.limit })
                .subscribe({
                    next: response => {
                        this.isLoading = false;
                        this.benachrichtigungen = <Benachrichtigung[]>response.records;
                    },
                });
        } else {
            this.getBenachrichtigungenSub = this.benachrichtigungenService
                .getBenachrichtigungen({ offset: this.offset, limit: this.limit })
                .subscribe({
                    next: response => {
                        this.isLoading = false;
                        this.benachrichtigungen = <Benachrichtigung[]>response.records;
                    },
                });
        }
    }

    allRead() {
        return this.benachrichtigungen?.every(n => n.gelesen == 1);
    }

    search() {
        this.isLoading = true;
        const searchTxt = this.searchCtrl.value;
        const filter = { offset: this.offset, limit: this.limit };
        if (this.onlyUnreadBenachrichtigungen) {
            filter['gelesen'] = 0;
        }
        if (this.searchCtrl.value) {
            filter['search'] = this.searchCtrl.value;
        }
        this.getBenachrichtigungenSub?.unsubscribe();
        this.getBenachrichtigungenSub = this.benachrichtigungenService.getBenachrichtigungen(filter).subscribe({
            next: response => {
                this.scrollDisabled = false;
                this.isLoading = false;
                this.benachrichtigungen = [...this.benachrichtigungen, ...(<Benachrichtigung[]>response.records)];
            },
        });
    }

    markAllAsRead() {
        this.isSaving = true;
        this.benachrichtigungenService.markAllBenachrichtigungAsRead().subscribe({
            next: () => {
                this.benachrichtigungenService.triggerReload();
                this.isSaving = false;
            },
            error: err => {
                this.messageService.errorMessage('Fehler beim Speichern!', err);
                this.isSaving = false;
            },
        });
    }

    isLink(benachrichtigung: Benachrichtigung): boolean {
        return (
            benachrichtigung?.gruppe_id > 0 ||
            benachrichtigung?.tierliste_id > 0 ||
            benachrichtigung?.gt_id > 0 ||
            benachrichtigung?.buchungs_id > 0 ||
            benachrichtigung?.ticket_id > 0
        );
    }

    openTicketDialog() {
        this.dialog.open(CreateTicketDialogComponent, { width: '100%', data: undefined });
    }
}
